/**
 * Hook for ConfirmModal state and handlers. Extracted to keep ConfirmModal under max-lines-per-function.
 */

import { useState, useEffect, ChangeEvent } from "react";
import { EButtonstyletype, EButtonVariant } from "../../Button";
import { buildConfirmModalHandlers } from "./confirmModalHandlers";
import { buildConfirmModalDerivedProps } from "./confirmModalButtonPropsHelpers";

type ConfirmModalStateParams = {
	isOpen: boolean;
	onClose: () => void;
	variant: "danger" | "warning" | "info";
	confirmButton?: {
		label: string;
		onClick: () => void | Promise<void>;
		variant?: EButtonVariant;
		styleType?: EButtonstyletype;
		disabled?: boolean;
		loading?: boolean;
		fullwidth?: boolean;
		leftIcon?: React.ReactNode;
		rightIcon?: React.ReactNode;
	};
	cancelButton?: {
		label: string;
		onClick?: () => void;
		variant?: EButtonVariant;
		styleType?: EButtonstyletype;
		fullwidth?: boolean;
	};
	requireConfirmation?: {
		enabled: boolean;
		label: string;
		placeholder: string;
		expectedValue: string;
		errorMessage?: string;
	};
	thirdButton?: {
		label: string;
		onClick: () => void;
		variant?: EButtonVariant;
		styleType?: EButtonstyletype;
		fullwidth?: boolean;
	};
	onConfirm?: () => void | Promise<void>;
	onCancel?: () => void;
	loading: boolean;
	closeOnBackdropClick: boolean;
};

export function useConfirmModalState(props: ConfirmModalStateParams): {
	confirmationValue: string;
	setConfirmationValue: (value: string) => void;
	handleClose: () => void;
	confirmButtonProps: ReturnType<typeof buildConfirmModalDerivedProps>["confirmButtonProps"];
	cancelButtonProps: ReturnType<typeof buildConfirmModalDerivedProps>["cancelButtonProps"];
	thirdButtonProps: ReturnType<typeof buildConfirmModalDerivedProps>["thirdButtonProps"];
	onConfirmationChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
} {
	const { isOpen, onClose, variant, confirmButton, cancelButton, requireConfirmation, thirdButton, onConfirm, onCancel, loading } = props;
	const [confirmationValue, setConfirmationValue] = useState("");
	useEffect(() => {
		if (isOpen) setConfirmationValue("");
	}, [isOpen]);
	const { handleConfirm, handleCancel, handleClose } = buildConfirmModalHandlers({
		requireConfirmation,
		confirmationValue,
		setConfirmationValue,
		confirmButton,
		onConfirm,
		cancelButton,
		onCancel,
		onClose,
	});
	const { confirmButtonProps, cancelButtonProps, thirdButtonProps } = buildConfirmModalDerivedProps({
		variant,
		requireConfirmation,
		confirmationValue,
		loading,
		confirmButton,
		onConfirm,
		handleConfirm,
		cancelButton,
		handleCancel,
		thirdButton,
	});
	const onConfirmationChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void =>
		setConfirmationValue(event.target.value);
	return { confirmationValue, setConfirmationValue, handleClose, confirmButtonProps, cancelButtonProps, thirdButtonProps, onConfirmationChange };
}
